import { ProductVariantInterface } from '../../interfaces/Productvariant.ts';
import classNames from 'classnames';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { formatCurrency } from '../../Utils/numberFormat.ts';
import { isEmpty } from '../../Utils/Object.ts';
import FidelityAdvantage from '../Common/FidelityAdvantage.tsx';
import styled from 'styled-components';

type propsVariantSelector = {
    variants: ProductVariantInterface[];
    setVariantSelected: Dispatch<SetStateAction<ProductVariantInterface>>;
    variantSelected: ProductVariantInterface;
    price: number | null;
};

const ProductVariantSelectorStyled = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    padding: 8px 12px;
    flex-direction: column;
    align-items: flex-start;
    border-radius: 4px;
    border: 1px solid ${props => props.theme.colors.greyLight};
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1) inset;
    max-height: 46px;
    transition: max-height 0.5s;
    overflow: hidden;
    cursor: pointer;

    ${props => props.theme.devices.desktop} {
        width: 553px;
    }

    &.opened {
        max-height: 1000px;
        transition: max-height 0.5s;

        .product-variant-options {
            visibility: visible;
            opacity: 1;
        }
    }

    .product-variant-options {
        flex-direction: column;
        margin-top: 10px;
        width: 100%;
        visibility: hidden;
        opacity: 0;
        transition:
            visibility 0.5s linear,
            opacity 0.5s linear;

        .product-variant-option {
            width: 100%;
            padding-bottom: 15px;
            padding-top: 15px;
        }

        .product-variant-option:not(:last-of-type) {
            border-bottom: none;

            ${props => props.theme.devices.desktop} {
                border-bottom: 1px ${props => props.theme.colors.greyLight} solid;
            }
        }
    }

    .product-variant-selected-container {
        display: flex;
        width: 100%;

        .product-variant-selected {
            display: flex;
            align-items: center;
            gap: 5px;
            align-self: stretch;
            width: 100%;

            .product-variant-placeholder {
                display: flex;
                width: 500px;
                align-items: center;
                align-self: stretch;
                justify-content: space-between;
                height: 30px;
            }

            .product-variant-selected-option {
                width: 100%;

                .product-variant-option {
                    height: 30px;
                }
            }
        }

        .product-variant-arrow {
            display: flex;
            width: 17px;
            height: ${props => props.theme.globalSize.size6};
            justify-content: center;
            align-items: center;
            position: absolute;
            right: 12px;
            top: 12px;

            .product-variant-arrow-icon {
                width: 11px;
                height: 11px;
                transform: rotate(135deg);
                flex-shrink: 0;
                border-radius: 2px;
                border-top: 3px solid ${props => props.theme.colors.greyDark};
                border-right: 3px solid ${props => props.theme.colors.greyDark};
                transform-origin: center;
                transition: transform 0.3s linear;

                &.opened {
                    transform: rotate(-45deg);
                    transition: transform 0.3s linear;
                }
            }
        }
    }
`;

const ProductVariantOptionStyled = styled.div`
    display: flex;
    align-items: center;
    align-self: stretch;
    cursor: pointer;
    width: 100%;

    ${props => props.theme.devices.desktop} {
        width: 500px;
        
    }

    .product-variant-option-availability {
        display: flex;
        flex: auto;
        justify-content: center;
    }

    .product-variant-option-price, .product-variant-option-emrys-coins {
        width: 85px;
    }

    .product-variant-option-price, .product-variant-option-availability {
        color: ${props => props.theme.colors.greyDark};
        font-family: ${props => props.theme.fonts.familyPrimary}
        font-size: ${props => props.theme.globalSize.size6};
        font-style: normal;
        font-weight: 400;
        line-height: 120%;
    }

    .product-variant-option-emrys-coins {
        display: flex;
        align-items: center;
        gap: 5px;

        > img {
            width: 30px;
            height: 30px;
        }

        > span {
            color: ${props => props.theme.colors.greyDark};
            font-family: ${props => props.theme.fonts.familyPrimary}
            font-size: ${props => props.theme.globalSize.size7};
            font-style: normal;
            font-weight: 400;
            line-height: 150%;
        }
    }
`;

export default function VariantSelector({ variants, setVariantSelected, variantSelected, price }: propsVariantSelector) {
    const [isOpened, setIsOpened] = useState(false);
    useEffect(() => {
        if (price && variants.length > 0) {
            const matchingVariant = variants.find(variant => variant.originalPrice === price);
            if (matchingVariant) {
                setVariantSelected(matchingVariant);
            }
        } else if (variants.length === 1) {
            setVariantSelected(variants[0]);
        } else if (variants) {
            const variantCheapest = variants
                .filter(variant => variant.onHand && variant.onHand > 0)
                .sort((variant, variant2) => variant.price - variant2.price)
                .at(0);
            if (variantCheapest) {
                setVariantSelected(variantCheapest);
            }
        }
    }, [price, variants, setVariantSelected]);

    //TODO maybe create hook to detect outside click
    document.body.addEventListener('click', () => {
        setIsOpened(false);
    });

    const displayVariant = (variant: ProductVariantInterface) => {
        return (
            <ProductVariantOptionStyled
                className='product-variant-option'
                onClick={() => {
                    setVariantSelected(variant);
                }}
                key={variant.id}
            >
                <div className='product-variant-option-price'>{formatCurrency(variant.originalPrice)}</div>
                <div className='product-variant-option-availability'>{displayAvailability(variant)}</div>
                <div className='product-variant-option-emrys-coins'>
                    {variant.emrysCoins && <FidelityAdvantage fidelityAdvantage={variant.emrysCoins} />}
                </div>
            </ProductVariantOptionStyled>
        );
    };

    const displayAvailability = (variant: ProductVariantInterface) => {
        if (variant.onHand > 0) {
            return <span className={'has-text-success'}>Disponible</span>;
        } else if (variant.preOrderable) {
            return <span className={'has-text-warning'}>Précommande</span>;
        } else {
            return <span className={'has-text-danger'}>Indisponible</span>;
        }
    };

    return (
        <>
            <ProductVariantSelectorStyled
                className={classNames('product-variant-selector', { opened: isOpened })}
                onClick={evt => {
                    evt.stopPropagation();
                    setIsOpened(!isOpened);
                }}
            >
                <div className='product-variant-selected-container'>
                    <div className='product-variant-selected'>
                        <div className={classNames('product-variant-placeholder', { 'is-hidden': !isEmpty(variantSelected) })}>
                            Sélectionner un montant
                        </div>
                        <div className={classNames('product-variant-selected-option', { 'is-hidden': isEmpty(variantSelected) })}>
                            {displayVariant(variantSelected)}
                        </div>
                    </div>

                    <div className='product-variant-arrow'>
                        <div className={classNames('product-variant-arrow-icon', { opened: isOpened })}> </div>
                    </div>
                </div>

                <div className={classNames('product-variant-options', { 'is-flex': isOpened })}>
                    {variants?.map(variant => displayVariant(variant))}
                </div>
            </ProductVariantSelectorStyled>
        </>
    );
}
